import { useCallback, useEffect, useState } from "react";
import { isAcceptedByceProgramIdentity } from "../../engine/byceKsuIdentity";


const headersFor = token => ({ Authorization: `Bearer ${token}` });

async function resolveFacilitatorProject({ apiUrl, slug, token }) {
  const response = await fetch(`${apiUrl}/projects/facilitator`, { headers: headersFor(token) });
  if (!response.ok) throw new Error("UNAVAILABLE");
  const payload = await response.json();
  const project = (payload.projects || []).find((item) => item.slug === slug);
  if (!project) throw new Error("DENIED");
  return project;
}

export function useProjectFacilitatorMembers({ apiUrl, slug, token }) {
  const [state, setState] = useState({ project: null, members: [], programs: [], loading: true, error: "" });
  useEffect(() => {
    let current = true;
    (async () => {
      try {
        const project = await resolveFacilitatorProject({ apiUrl, slug, token });
        const headers = headersFor(token);
        const [rosterResponse, progressResponse] = await Promise.all([
          fetch(`${apiUrl}/projects/${project.id}/facilitator/roster`, { headers }),
          fetch(`${apiUrl}/projects/${project.id}/facilitator/program-progress`, { headers }),
        ]);
        if (!rosterResponse.ok || !progressResponse.ok) throw new Error("UNAVAILABLE");
        const roster = await rosterResponse.json();
        const progress = await progressResponse.json();
        const progressByMember = new Map(
          (progress.participants || []).map((item) => [item.participantRef, item])
        );
        const members = (roster.members || []).map((member) => ({
          ...member,
          programs: progressByMember.get(member.participantRef)?.programs || [],
          historicalProgramAttempts:
            progressByMember.get(member.participantRef)?.historicalProgramAttempts || [],
        }));
        const evidenceEntries = await Promise.all(members.flatMap(member =>
          (progress.programs || []).map(async program => {
            const query = new URLSearchParams({
              programSlug: program.slug,
              programVersion: program.version,
              membershipId: member.membershipId,
              readShape: "summary",
            });
            const evidenceResponse = await fetch(
              `${apiUrl}/projects/${project.id}/facilitator/program-evidence?${query}`,
              { headers }
            );
            return [member.membershipId, program.id, evidenceResponse.ok ? await evidenceResponse.json() : null];
          })
        ));
        const evidenceByMember = evidenceEntries.reduce((result, [membershipId, programId, evidence]) => {
          if (!result[membershipId]) result[membershipId] = {};
          result[membershipId][programId] = evidence;
          return result;
        }, {});
        const membersWithEvidence = members.map(member => ({
          ...member,
          evidenceByProgram: evidenceByMember[member.membershipId] || {},
        }));
        if (current) setState({ project: roster.project, members: membersWithEvidence, programs: progress.programs || [], loading: false, error: "" });
      } catch (error) {
        if (current) setState({
          project: null, members: [], programs: [], loading: false,
          error: error.message === "DENIED"
            ? "This Project is not available for facilitation."
            : "Project members are temporarily unavailable.",
        });
      }
    })();
    return () => { current = false; };
  }, [apiUrl, slug, token]);
  return state;
}

export function useProjectFacilitatorMember({ apiUrl, slug, memberRef, token }) {
  const [state, setState] = useState({ detail: null, attentionReviewsByProgram: {},
    reflectionOverridesByProgram: {}, engagementAdjustmentsByProgram: {}, loading: true, error: "" });
  useEffect(() => {
    let current = true;
    (async () => {
      try {
        const project = await resolveFacilitatorProject({ apiUrl, slug, token });
        const response = await fetch(
          `${apiUrl}/projects/${project.id}/facilitator/members/${encodeURIComponent(memberRef)}`,
          { headers: headersFor(token) }
        );
        if (!response.ok) throw new Error(response.status === 404 ? "DENIED" : "UNAVAILABLE");
        const detail = await response.json();
        const entries = await Promise.all((detail.programs || []).map(async program => {
          const query = new URLSearchParams({
            programSlug: program.slug,
            programVersion: program.version,
            membershipId: memberRef,
          });
          const evidenceResponse = await fetch(
            `${apiUrl}/projects/${project.id}/facilitator/program-evidence?${query}`,
            { headers: headersFor(token) }
          );
          return [program.id, evidenceResponse.ok ? await evidenceResponse.json() : null];
        }));
        const reviewEntries = await Promise.all((detail.programs || [])
          .filter(isAcceptedByceProgramIdentity)
          .map(async program => {
            const query = new URLSearchParams({ programSlug: program.slug,
              programVersion: program.version, membershipId: memberRef });
            const response = await fetch(
              `${apiUrl}/projects/${project.id}/facilitator/byce-g3-attention-reviews?${query}`,
              { headers: headersFor(token) }
            );
            return [program.id, response.ok ? await response.json() : null];
          }));
        const reflectionOverrideEntries = await Promise.all((detail.programs || [])
          .filter(isAcceptedByceProgramIdentity)
          .map(async program => {
            const query = new URLSearchParams({ programSlug: program.slug,
              programVersion: program.version, membershipId: memberRef });
            const response = await fetch(
              `${apiUrl}/projects/${project.id}/facilitator/byce-g3-reflection-overrides?${query}`,
              { headers: headersFor(token) }
            );
            return [program.id, response.ok ? await response.json() : null];
          }));
        const engagementAdjustmentEntries = await Promise.all((detail.programs || [])
          .filter(isAcceptedByceProgramIdentity)
          .map(async program => {
            const query = new URLSearchParams({ programSlug: program.slug,
              programVersion: program.version, membershipId: memberRef });
            const response = await fetch(
              `${apiUrl}/projects/${project.id}/facilitator/byce-g3-engagement-adjustment?${query}`,
              { headers: headersFor(token) }
            );
            return [program.id, response.ok ? await response.json() : null];
          }));
        if (current) setState({
          detail: { ...detail, evidenceByProgram: Object.fromEntries(entries) },
          attentionReviewsByProgram: Object.fromEntries(reviewEntries),
          reflectionOverridesByProgram: Object.fromEntries(reflectionOverrideEntries),
          engagementAdjustmentsByProgram: Object.fromEntries(engagementAdjustmentEntries),
          loading: false,
          error: "",
        });
      } catch (error) {
        if (current) setState({
          detail: null, attentionReviewsByProgram: {}, reflectionOverridesByProgram: {},
          engagementAdjustmentsByProgram: {}, loading: false,
          error: error.message === "DENIED"
            ? "This Project member is not available."
            : "Project member details are temporarily unavailable.",
        });
      }
    })();
    return () => { current = false; };
  }, [apiUrl, memberRef, slug, token]);
  const recordAttentionReview = useCallback(async ({ projectId, program, event, rationale,
    idempotencyKey }) => {
    const response = await fetch(`${apiUrl}/projects/${projectId}/facilitator/byce-g3-attention-reviews`, {
      method: "POST",
      headers: { ...headersFor(token), "Content-Type": "application/json" },
      body: JSON.stringify({
        programSlug: program.slug, programVersion: program.version, membershipId: memberRef,
        eventKey: event.eventKey,
        expectedCurrentDecisionId: event.currentDecisionId,
        idempotencyKey,
        rationale,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.code || "BYCE_G3_ATTENTION_REVIEW_UNAVAILABLE");
    setState(current => ({ ...current,
      attentionReviewsByProgram: {
        ...current.attentionReviewsByProgram,
        [program.id]: {
          ...(current.attentionReviewsByProgram[program.id] || {}),
          events: (current.attentionReviewsByProgram[program.id]?.events || [])
            .map(row => row.eventKey === payload.review.eventKey ? payload.review : row),
        },
      },
    }));
    return payload;
  }, [apiUrl, memberRef, token]);
  const recordReflectionOverride = useCallback(async ({ projectId, program, item, points,
    rationale, idempotencyKey }) => {
    const response = await fetch(`${apiUrl}/projects/${projectId}/facilitator/byce-g3-reflection-overrides`, {
      method: "POST", headers: { ...headersFor(token), "Content-Type": "application/json" },
      body: JSON.stringify({ programSlug: program.slug, programVersion: program.version,
        membershipId: memberRef, programStepKey: item.stepKey, points,
        expectedCurrentDecisionId: item.currentDecisionId, idempotencyKey, rationale }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.code || "BYCE_G3_REFLECTION_OVERRIDE_UNAVAILABLE");
    const query = new URLSearchParams({ programSlug: program.slug, programVersion: program.version,
      membershipId: memberRef, readShape: "full" });
    const evidenceResponse = await fetch(
      `${apiUrl}/projects/${projectId}/facilitator/program-evidence?${query}`,
      { headers: headersFor(token) }
    );
    const evidence = evidenceResponse.ok ? await evidenceResponse.json() : null;
    setState(current => ({ ...current,
      detail: evidence ? { ...current.detail, evidenceByProgram: {
        ...current.detail.evidenceByProgram, [program.id]: evidence,
      } } : current.detail,
      reflectionOverridesByProgram: {
        ...current.reflectionOverridesByProgram,
        [program.id]: { ...(current.reflectionOverridesByProgram[program.id] || {}),
          ...payload.overrides },
      },
    }));
    return payload;
  }, [apiUrl, memberRef, token]);
  const recordEngagementAdjustment = useCallback(async ({ projectId, program, deductionPoints,
    basisReviewDecisionIds, rationale, expectedCurrentDecisionId, idempotencyKey }) => {
    const response = await fetch(`${apiUrl}/projects/${projectId}/facilitator/byce-g3-engagement-adjustment`, {
      method: "POST", headers: { ...headersFor(token), "Content-Type": "application/json" },
      body: JSON.stringify({ programSlug: program.slug, programVersion: program.version,
        membershipId: memberRef, deductionPoints, basisReviewDecisionIds,
        expectedCurrentDecisionId, idempotencyKey, rationale }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.code || "BYCE_G3_ENGAGEMENT_ADJUSTMENT_UNAVAILABLE");
    const query = new URLSearchParams({ programSlug: program.slug, programVersion: program.version,
      membershipId: memberRef, readShape: "full" });
    const [evidenceResponse, reviewResponse] = await Promise.all([
      fetch(`${apiUrl}/projects/${projectId}/facilitator/program-evidence?${query}`,
        { headers: headersFor(token) }),
      fetch(`${apiUrl}/projects/${projectId}/facilitator/byce-g3-attention-reviews?${query}`,
        { headers: headersFor(token) }),
    ]);
    const evidence = evidenceResponse.ok ? await evidenceResponse.json() : null;
    const reviews = reviewResponse.ok ? await reviewResponse.json() : null;
    setState(current => ({ ...current,
      detail: evidence ? { ...current.detail, evidenceByProgram: {
        ...current.detail.evidenceByProgram, [program.id]: evidence,
      } } : current.detail,
      attentionReviewsByProgram: reviews ? {
        ...current.attentionReviewsByProgram, [program.id]: reviews,
      } : current.attentionReviewsByProgram,
      engagementAdjustmentsByProgram: {
        ...current.engagementAdjustmentsByProgram, [program.id]: payload.adjustment,
      },
    }));
    return payload;
  }, [apiUrl, memberRef, token]);
  const downloadParticipantExport = useCallback(async ({ projectId, program }) => {
    const query = new URLSearchParams({ programSlug: program.slug,
      programVersion: program.version, membershipId: memberRef, format: "csv" });
    const response = await fetch(
      `${apiUrl}/projects/${projectId}/facilitator/byce-g3-participant-export?${query}`,
      { headers: headersFor(token) }
    );
    if (!response.ok) throw new Error("BYCE_G3_PARTICIPANT_EXPORT_UNAVAILABLE");
    const blob = await response.blob();
    const disposition = response.headers.get("Content-Disposition") || "";
    const filename = disposition.match(/filename="([^"]+)"/)?.[1] || "byce-participant-evidence.csv";
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = filename; link.click(); URL.revokeObjectURL(url);
  }, [apiUrl, memberRef, token]);
  return { ...state, recordAttentionReview, recordReflectionOverride, recordEngagementAdjustment,
    downloadParticipantExport };
}
